<script setup lang="ts">
import { computed, ref } from 'vue';

import { useCommentsStore } from '@/stores';
import type { CommentDto } from '@/services/types/api.types';

import CommentSummaryEntry from '@/components/CommentSummaryEntry.vue';
import CommentSummaryEntryAdd from '@/components/CommentSummaryEntryAdd.vue';
import { confirmDelete } from '@/helpers/dialogs.helper';

const commentsStore = useCommentsStore();

const props = defineProps<{
  comments: CommentDto[];
}>();

const columns = ref([
  { name: 'id', label: 'ID', align: 'left', field: (row: CommentDto) => row.id },
  {
    name: 'comment',
    label: 'COMMENTAIRE',
    align: 'left',
    field: (row: CommentDto) => row.comment,
    style: 'max-width: 200px',
  },
  { name: 'summary', label: 'RÉSUMÉ', align: 'left', field: (row: CommentDto) => row.summary },
]);

const summaryList = computed(() => {
  const summaryCount: Record<string, number> = props.comments
    .map((item) => (item.summary ? item.summary.map((summary) => summary.text) : []))
    .flat()
    .reduce((acc: Record<string, number>, text) => {
      acc[text] = (acc[text] || 0) + 1;
      return acc;
    }, {});
  return summaryCount;
});

const handleSaveSummaryEntry = async (commentId: number, summaryId: number, text: string) => {
  console.debug('handleSaveSummaryEntry', commentId, summaryId, text);
  await commentsStore.saveSummaryEntry(commentId, summaryId, text);
};

const handleDeleteSummaryEntry = (commentId: number, summaryId: number) => {
  console.debug('handleDeleteSummaryEntry', summaryId);
  confirmDelete('ce résumé').onOk(() => {
    commentsStore.deleteSummaryEntry(commentId, summaryId);
  });
};
</script>

<template>
  <q-expansion-item label="Sommaire" class="q-mb-md" dark dense header-class="bg-secondary">
    <q-card>
      <q-card-section>
        <q-list dense>
          <q-item v-for="(count, text) in summaryList" :key="text" dense>
            <q-item-section>
              <q-item-label>{{ text }} ({{ count }})</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>
    </q-card>
  </q-expansion-item>
  <q-table
    :columns="columns"
    :rows="comments"
    :pagination="{ rowsPerPage: 0, sortBy: 'id' }"
    :visible-columns="['comment', 'summary']"
    table-header-class="bg-secondary text-white"
    dense
    wrap-cells
    class="comment-detail-list--table"
  >
    <template v-slot:body-cell-summary="props">
      <q-td :props="props">
        <div v-for="entry in props.row.summary" :key="entry.id">
          <comment-summary-entry
            :entry="entry"
            @save="(id, text) => handleSaveSummaryEntry(props.row.id, id, text)"
            @delete="(id) => handleDeleteSummaryEntry(props.row.id, id)"
          />
        </div>
        <comment-summary-entry-add @save="(id, text) => handleSaveSummaryEntry(props.row.id, id, text)" />
      </q-td>
    </template>
  </q-table>
</template>

<style lang="scss"></style>
